import type { AdminInspectorDetailDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Orders tab of the inspector detail drawer (AUT-125). Reads the rollup
 * straight off the detail DTO (`ordersSummary.{total,active,byStatus}`)
 * — no extra fetch (the full per-order list is an ADMIN-15+ surface).
 *
 * The `active` count is the architect's lock for the deactivate dialog
 * warning (🟠 #3), but it is also surfaced here as a top-level callout
 * so the admin sees it before they reach the footer.
 */
export const OrdersTab = ({ inspector }: { inspector: AdminInspectorDetailDto }): ReactElement => {
  const { t } = useTranslation();
  const byStatus = Object.entries(inspector.ordersSummary.byStatus);

  if (inspector.ordersSummary.total === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('inspectors.ordersTab.empty')}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Typography variant="subtitle2">
          {t('inspectors.ordersTab.total', { count: inspector.ordersSummary.total })}
        </Typography>
        <Typography variant="body2" color="warning.main">
          {t('inspectors.ordersTab.active', { count: inspector.ordersSummary.active })}
        </Typography>
      </Box>
      {byStatus.length > 0 ? (
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('inspectors.ordersTab.byStatus')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.75 }}>
            {byStatus.map(([status, count]) => (
              <Chip
                key={status}
                size="small"
                variant="outlined"
                label={`${t(`orders.status.${status}`, { defaultValue: status })}: ${count}`}
              />
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};
